import React from "react";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {useTranslation} from "react-i18next";
import "../../i18n/config";
import {useSetStyleRegistory} from "../../hooks/useSetStyleRegistory";
import {FunnelKey} from "../../models/Keys";
import {Tooltip} from "../common/Tooltip";
import {KeyboardEventUtil} from "../../utils/KeyboardEventUtil";
import {MainFunnelCell, MainFunnelLine} from "../../models/MainFunnelCell";
import {FunnelHeaders} from "../../models/Funnel";
import {InlineEditor} from "./InlineEditor";
import {fireCopyEvent} from "../../utils/browser";

type MainFunnelProps = {
    main_funnel_cell: MainFunnelCell;
    readOnly: boolean;
    onSelectCell: (funnel_key: FunnelKey) => void;
    onNameChange: (funnel_key: FunnelKey, new_name: string) => void;

    sortId: string;
    onRemove: (key: FunnelKey) => void;
    onHide: (key: FunnelKey) => void;
    onShow: (key: FunnelKey) => void;
}

const RCMainFunnelCell = (props: MainFunnelProps) => {
    const {t} = useTranslation();
    const [isEdit, setIsEdit] = React.useState(false);
    const [show_collaborator_label, setShowCollaboratorLabel] = React.useState(false);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        //transition,
    } = useSortable({id: props.sortId});

    if (transform) {
        // x 方向に固定
        transform.y = 0;
        transform.scaleX = 1;
        transform.scaleY = 1;
    }

    const dispatch_style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        // transition: transition ?? '',
    };

    const setStyleRegistry = useSetStyleRegistory();
    setStyleRegistry.dispatch(props.main_funnel_cell.Funnel.Key, dispatch_style);

    const funnel_cell_style = {
        ...dispatch_style,
        backgroundColor: props.main_funnel_cell.State.BackgroundColor,
        '--background-color': props.main_funnel_cell.State.BackgroundColor,
    } as React.CSSProperties; /* CSSProperties の型定義上、 custom property は指定できないので as で抑制 */


    const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (props.main_funnel_cell.State.IsSelected) {
            !props.readOnly && setIsEdit(true);
        } else {
            props.onSelectCell(props.main_funnel_cell.Funnel.Key);
        }

        e.stopPropagation(); // inhibit clearSelect
    }

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        if (KeyboardEventUtil.isStartEditKey(e.nativeEvent)) {
            !props.readOnly && setIsEdit(true);
            e.preventDefault();
        }

        if (KeyboardEventUtil.isCopyKey(e.nativeEvent)) {
            if (ref.current) {
                fireCopyEvent(ref.current);
            }
            e.preventDefault();
        }
    }

    const handleCopy: React.ClipboardEventHandler = (e) => {
        e.clipboardData.setData('text/plain', props.main_funnel_cell.Funnel.Name);
        e.preventDefault();
    }

    const handleEyeClick = () => {
        if (props.main_funnel_cell.Funnel.IsHidden) {
            props.onShow(props.main_funnel_cell.Funnel.Key);
        } else {
            props.onHide(props.main_funnel_cell.Funnel.Key);
        }
    }

    const ref = React.useRef<HTMLDivElement>(null);
    const setSetNodeRef = (node: HTMLElement | null) => {
        setNodeRef(node);

        // @ts-ignore  TS2540: Cannot assign to 'current' because it is a read-only property.
        ref.current = node;
    }

    React.useEffect(() => {
        if (props.main_funnel_cell.State.IsSelected) {
            ref.current?.focus();
        }
    }, [props.main_funnel_cell.State.IsSelected]);

    return (
        <div
            {...attributes}
            className="funnel_cell"
            ref={setSetNodeRef}
            data-is-selected={props.main_funnel_cell.State.IsSelected}
            data-collaborator-selected={0 < props.main_funnel_cell.State.Collaborators.length}
            style={funnel_cell_style}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onCopy={handleCopy}
            onMouseEnter={(e) => setShowCollaboratorLabel(true)}
            onMouseLeave={(e) => setShowCollaboratorLabel(false)}
            role={undefined} /* attributes でセットされるのを抑制  */
            tabIndex={0} /* hack for capture keyboard event */
        >
            {props.main_funnel_cell.Funnel.IsHidden ? <span className="hidden_funnel"/>
                : <InlineEditor value={props.main_funnel_cell.Funnel.Name} isEdit={isEdit}
                                onChangeValue={value => {
                                    if (props.main_funnel_cell.Funnel.Name !== value) {
                                        props.onNameChange(props.main_funnel_cell.Funnel.Key, value);
                                    }
                                    ref.current?.focus();
                                }}
                                onLeave={() => {
                                    setIsEdit(false);
                                }}
                />}
            <div className="grab-ui" data-enabled={!props.readOnly} onClick={e => e.stopPropagation()}>
                <Tooltip title={t("左右移動")}>
                    <span className="grab-ui-button horizontal_move" {...listeners}/>
                </Tooltip>
                <Tooltip title={props.main_funnel_cell.Funnel.IsHidden ? t("表示") : t("非表示")}>
                    <span className={`grab-ui-button ${props.main_funnel_cell.Funnel.IsHidden ? 'display' : 'hidden'}`}
                          onClick={handleEyeClick}/>
                </Tooltip>
                <Tooltip title={t("削除")}>
                    <span className="grab-ui-button delete"
                          onClick={() => props.onRemove(props.main_funnel_cell.Funnel.Key)}/>
                </Tooltip>
            </div>
            <div className="collaborator_label" style={{display: show_collaborator_label ? 'block': 'none'}}>
                {props.main_funnel_cell.State.Collaborators.join(", ")}
            </div>
        </div>
    )
}

export type RCMainFunnelLineProps = {
    funnel_headers: FunnelHeaders;
    main_funnel_line: MainFunnelLine;
    readOnly: boolean;
    onSelectCell: (key: FunnelKey) => void;
    onNameChange: (key: FunnelKey, new_name: string) => void;
    onRemove: (key: FunnelKey) => void;
    onHide: (key: FunnelKey) => void;
    onShow: (key: FunnelKey) => void;
    onDragStart: () => void;
    onDragEnd: (old_key: FunnelKey, new_key: FunnelKey) => void;
};

export const RCMainFunnelLine = (props: RCMainFunnelLineProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (_: DragStartEvent) => {
        props.onDragStart();
    }

    const keys = props.funnel_headers.Keys;
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            props.onDragEnd(active.id as FunnelKey, over.id as FunnelKey);
        } else {
            props.onDragEnd(active.id as FunnelKey, active.id as FunnelKey);
        }
    }

    const style = {
        display: "grid" as React.CSSProperties['display'],
        gridTemplateColumns: props.funnel_headers.GridTemplateColumns,
        gridGap: 10,
    };

    return (
        <div className="funnel" style={style}>
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                        onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={keys} strategy={horizontalListSortingStrategy}>
                    {props.main_funnel_line.mapCells(
                        cell =>
                            <RCMainFunnelCell key={cell.Funnel.Key}
                                              main_funnel_cell={cell}
                                              sortId={cell.Funnel.Key}
                                              readOnly={props.readOnly}
                                              onSelectCell={props.onSelectCell}
                                              onNameChange={props.onNameChange}

                                              onRemove={props.onRemove}
                                              onHide={props.onHide}
                                              onShow={props.onShow}
                            />
                    )}
                </SortableContext>
            </DndContext>
        </div>
    );
}
